import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { SettingService } from './setting.service';

/** Default path yang di-handle oleh SsoCallbackComponent */
const SSO_REDIRECT_PATH = '/sso/callback';
const SOKRATES_SSO_SCRIPT_URL = 'https://files.sokrates.co.id/sokrates-sso.js';

const SWITCH_IN_KEY = 'sso_switch_in';
const CARRY_FROM_KEY = 'sso_carry_from';
const CARRY_ATTEMPT_KEY = 'sso_carry_attempt';
// A switch-in not used by then is stale (e.g. the tab was left on another page).
const SWITCH_IN_MAX_AGE_MS = 5 * 60 * 1000;

// How long startup waits for the product's enforce-SSO setting before using environment.sso.enforce.
const ENFORCE_TIMEOUT_MS = 2500;

interface SokratesSsoConfig {
  apiBaseUrl: string;
  ssoFrontendUrl: string;
  productCode: string;
  redirectPath?: string;
}

export interface SsoRedirectCallbackResult {
  code: string;
  pkceVerifier: string;
  tenantUuid: string;
  state: string;
  autoLogin: boolean;
  /** The code is for a switched account (SDK >= 1.3.0). */
  switched?: boolean;
  app_claims?: any;
  impersonation?: any;
}

export interface SsoSwitchResult {
  code: string;
  pkceVerifier: string;
  tenantUuid: string;
  state: string;
  switched: boolean;
}

/** How another Sokrates app opened School Program (see captureSwitchIn). */
export interface SsoSwitchIn {
  tenantUuid: string | null;
  /** That app's origin, when it was switched to another account: School Program takes the same switch. */
  carryFrom: string | null;
  /** That app's origin: SSO signs School Program in with the account that app uses. */
  fromOrigin: string | null;
}

// Mirrors sokrates-sso.d.ts in sokrates-sso-frontend-v2 (the SDK is loaded as a script).
interface SokratesSsoClient {
  // fromOrigin: SDK >= 1.4.0 (an older SDK ignores it).
  loginWithRedirect(options?: { tenantUuid?: string | null; redirectUri?: string; prompt?: 'auto' | null; fromOrigin?: string | null }): Promise<void>;
  // SDK >= 1.3.0.
  carrySwitchWithRedirect?(options: { fromOrigin: string; tenantUuid?: string | null; redirectUri?: string }): Promise<void>;
  handleRedirectCallback(callbackUrl?: string): Promise<SsoRedirectCallbackResult>;
  switchAccountWithPopup(options?: { tenantUuid?: string | null }): Promise<SsoSwitchResult>;
  restoreOriginalAccountWithPopup(options?: { tenantUuid?: string | null }): Promise<SsoSwitchResult>;
  logout(options?: { sessionId?: string }): Promise<any>;
  getAccountUrl(options?: { sessionId?: string; path?: string }): string;
  // Access token API (SDK >= 1.0.0); optional so an older cached SDK keeps working on the monolith token.
  setAccessToken?(token: string | null | undefined, expiresInSeconds: number | null | undefined): void;
  clearAccessToken?(): void;
  // SDK >= 1.2.0.
  handleUnauthorized?(): Promise<'relogin' | 'logged_out'>;
}

interface SokratesSsoWindow extends Window {
  SokratesSSO?: {
    create: (config: SokratesSsoConfig) => SokratesSsoClient;
    mountButton: (target: string | Element, config: SokratesSsoConfig, options: any) => HTMLButtonElement;
    createButton: (config: SokratesSsoConfig, options: any) => HTMLButtonElement;
  };
}

function environmentUrl(config: { url: string; secure: boolean }): string {
  const protocol = config.secure ? 'https://' : 'http://';
  return protocol + config.url;
}

/**
 * Thin wrapper over the Sokrates SSO SDK (sokrates-sso.js): loads the script once and keeps one
 * client, so login, account switch, logout and the SSO access token share the same state.
 */
@Injectable({
  providedIn: 'root'
})
export class SokratesSsoScriptService {
  private scriptPromise: Promise<void> | null = null;
  private client: SokratesSsoClient | null = null;
  /** Enforce SSO from sokrates-admin (loadEnforce); null until read. */
  private enforced: boolean | null = null;

  private readonly config: SokratesSsoConfig = {
    // The SDK calls `${apiBaseUrl}/public/...` (session token, logout), under the SSO service prefix.
    apiBaseUrl: `${environmentUrl(environment.api)}/sso-new-http`,
    ssoFrontendUrl: environment.sso.url,
    productCode: environment.sso.product_code,
    redirectPath: SSO_REDIRECT_PATH,
  };

  constructor(
      private settingService: SettingService,
  ) { }

  /**
   * Starts downloading the SDK without waiting for it, so the login page and the SSO callback find
   * it loaded (or in flight) instead of fetching it only when they need it.
   */
  preload(): void {
    this.loadScript().catch((error) => {
      // A failed preload is retried by the first real use.
      this.scriptPromise = null;
      console.warn(error);
    });
  }

  /**
   * Reads, once at startup, whether this product signs in through SSO only (Enforce SSO in
   * sokrates-admin, served by tenant-http so it can still be turned off while SSO has trouble).
   * Without an answer in time (tenant-http down, slow network) environment.sso.enforce applies.
   */
  async loadEnforce(): Promise<void> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ENFORCE_TIMEOUT_MS);
    try {
      const productCode = encodeURIComponent(environment.sso.product_code);
      const response = await fetch(`${environmentUrl(environment.api)}/tenant-http/v1/products/${productCode}/sso`, {
        signal: controller.signal,
      });
      const body = await response.json();
      if (body?.status === true && typeof body.data?.enforce_sso === 'boolean') {
        this.enforced = body.data.enforce_sso;
      }
    } catch (error) {
      console.warn('SSO setting not read, using environment.sso.enforce', error);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Whether this product signs in through SSO only: sokrates-admin's setting, else
   * environment.sso.enforce. Off, the legacy login form is used and nothing goes through SSO:
   * other apps open with the token in the URL and a switch-in from them is ignored.
   */
  isEnforced(): boolean {
    return this.enforced ?? environment.sso.enforce;
  }

  async getClient(): Promise<SokratesSsoClient> {
    await this.loadScript();

    if (!this.client) {
      this.client = this.ssoWindow.SokratesSSO!.create(this.config);
    }

    return this.client;
  }

  async createButton(options: any): Promise<HTMLButtonElement> {
    await this.loadScript();
    const client = await this.getClient();

    return this.ssoWindow.SokratesSSO!.createButton(this.config, {
      ...options,
      client,
    });
  }

  /**
   * Redirect langsung ke SSO portal tanpa popup. State PKCE disimpan di sessionStorage oleh SDK.
   * Setelah login, SSO akan redirect kembali ke `redirectPath` (/sso/callback).
   * prompt 'auto': opened from another app, SSO continues without the picker, with the account of
   * the app at fromOrigin (else the active account).
   */
  async loginWithRedirect(options: { tenantUuid?: string | null; prompt?: 'auto' | null; fromOrigin?: string | null } = {}): Promise<void> {
    const client = await this.getClient();
    const tenantUuid = options.tenantUuid ?? this.settingService.getTenantUuid();
    return client.loginWithRedirect({ tenantUuid, prompt: options.prompt ?? null, fromOrigin: options.fromOrigin ?? null });
  }

  /**
   * Dipanggil oleh SsoCallbackComponent setelah SSO redirect balik ke app.
   * Mengembalikan { code, pkceVerifier, tenantUuid, state } untuk ditukar ke backend.
   */
  async handleRedirectCallback(callbackUrl?: string): Promise<SsoRedirectCallbackResult> {
    const client = await this.getClient();
    return client.handleRedirectCallback(callbackUrl);
  }

  /** Ends the SSO session this origin uses (resolved by SSO from the browser's cookie). */
  async logout(): Promise<any> {
    const client = await this.getClient();
    return client.logout({});
  }

  /** The SSO account page (profile, password, devices) of the account this origin signed in with. */
  async getAccountUrl(): Promise<string> {
    const client = await this.getClient();
    return client.getAccountUrl({});
  }

  /**
   * The SDK's answer to a request rejected with this session: 'relogin' (sign in again) or
   * 'logged_out' (rejected right after signing in, so the SDK ended the SSO session to stop a loop).
   */
  async handleUnauthorized(): Promise<'relogin' | 'logged_out'> {
    const client = await this.getClient();
    if (client.handleUnauthorized) {
      return client.handleUnauthorized();
    }
    client.clearAccessToken?.();
    return 'relogin';
  }

  /**
   * Hands the SSO access token from the code exchange to the SDK, which keeps it for this tab and
   * refreshes it with the sso_master cookie. It also marks the moment of signing in, which
   * handleUnauthorized uses to tell a fresh rejection (a loop) from an expired session.
   */
  setAccessToken(token: string | null | undefined, expiresInSeconds: number | null | undefined): void {
    this.getClient()
      .then((client) => client.setAccessToken?.(token, expiresInSeconds))
      .catch((error) => console.error('SSO access token not stored', error));
  }

  async clearAccessToken(): Promise<void> {
    try {
      const client = await this.getClient();
      client.clearAccessToken?.();
    } catch (error) {
      console.error('SSO access token not cleared', error);
    }
  }

  /**
   * Buka popup SSO untuk ganti akun (switch account).
   * Mengembalikan { code, pkceVerifier, tenantUuid, state, switched } untuk ditukar ke backend.
   */
  async switchAccountWithPopup(): Promise<SsoSwitchResult> {
    const client = await this.getClient();
    return client.switchAccountWithPopup({ tenantUuid: this.settingService.getTenantUuid() });
  }

  /**
   * Buka popup SSO untuk kembali ke akun asli setelah switch.
   */
  async restoreOriginalAccountWithPopup(): Promise<SsoSwitchResult> {
    const client = await this.getClient();
    return client.restoreOriginalAccountWithPopup({ tenantUuid: this.settingService.getTenantUuid() });
  }

  /**
   * Another Sokrates app opens School Program with ?sso_switch=1 (&tenant_uuid, &sso_from, &carry_from). Read
   * at startup, before the server-select and login redirects drop the query, and kept for /login.
   */
  captureSwitchIn(): void {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('sso_switch') !== '1') {
        return;
      }
      sessionStorage.setItem(SWITCH_IN_KEY, JSON.stringify({
        tenantUuid: url.searchParams.get('tenant_uuid'),
        carryFrom: url.searchParams.get('carry_from'),
        fromOrigin: url.searchParams.get('sso_from'),
        at: Date.now(),
      }));
      ['sso_switch', 'tenant_uuid', 'carry_from', 'sso_from'].forEach((name) => url.searchParams.delete(name));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      console.warn('SSO switch-in not read', error);
    }
  }

  hasSwitchIn(): boolean {
    return this.readSwitchIn() !== null;
  }

  /** The pending switch-in, consumed: /login signs in with it once. */
  takeSwitchIn(): SsoSwitchIn | null {
    const switchIn = this.readSwitchIn();
    sessionStorage.removeItem(SWITCH_IN_KEY);
    return switchIn;
  }

  /** The app whose switch School Program takes over right after signing in (set before the SSO redirect). */
  setCarryFrom(origin: string | null): void {
    if (origin) {
      sessionStorage.setItem(CARRY_FROM_KEY, origin);
    } else {
      sessionStorage.removeItem(CARRY_FROM_KEY);
    }
  }

  takeCarryFrom(): string | null {
    const origin = sessionStorage.getItem(CARRY_FROM_KEY);
    sessionStorage.removeItem(CARRY_FROM_KEY);
    return origin;
  }

  /**
   * Takes over `fromOrigin`'s switch once School Program signed in as the actor. Back on the
   * callback with the switched code, or with an error when there is nothing to carry
   * (takeCarryAttempt tells which).
   */
  async carrySwitchWithRedirect(fromOrigin: string): Promise<void> {
    const client = await this.getClient();
    if (!client.carrySwitchWithRedirect) {
      throw new Error('This SSO SDK cannot carry a switch.');
    }
    sessionStorage.setItem(CARRY_ATTEMPT_KEY, '1');
    await client.carrySwitchWithRedirect({ fromOrigin });
  }

  takeCarryAttempt(): boolean {
    const attempted = sessionStorage.getItem(CARRY_ATTEMPT_KEY) === '1';
    sessionStorage.removeItem(CARRY_ATTEMPT_KEY);
    return attempted;
  }

  private readSwitchIn(): SsoSwitchIn | null {
    if (!this.isEnforced()) {
      return null;
    }
    try {
      const stored = JSON.parse(sessionStorage.getItem(SWITCH_IN_KEY) || 'null');
      if (!stored || Date.now() - Number(stored.at) > SWITCH_IN_MAX_AGE_MS) {
        return null;
      }
      return { tenantUuid: stored.tenantUuid || null, carryFrom: stored.carryFrom || null, fromOrigin: stored.fromOrigin || null };
    } catch (error) {
      return null;
    }
  }

  private loadScript(): Promise<void> {
    if (this.ssoWindow.SokratesSSO) {
      return Promise.resolve();
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SOKRATES_SSO_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Sokrates SSO script.'));
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private get ssoWindow(): SokratesSsoWindow {
    return window as SokratesSsoWindow;
  }
}
